/**
 * Termin-Repository.
 *
 * Besonderheit gegenueber den anderen Repositories: Serien werden nie direkt
 * geschrieben. Die drei Bearbeitungsmodi erzeugen in `lib/recurrence/edit.ts`
 * einen Schreibplan, und der wird hier in EINER Transaktion angewendet - damit
 * es keinen Zwischenzustand gibt, in dem die Serie schon abgeschnitten, die
 * Nachfolgeserie aber noch nicht angelegt ist.
 */
import { db } from '@/db/schema';
import { addDays, datePart, formatDate, nowInstant, toLocalDateTime } from '@/lib/datetime';
import { bestaetigungsKey } from '@/domain/calendar/bestaetigung';
import { newId } from '@/lib/utils/id';
import {
  deleteAllOccurrences, deleteSingleOccurrence, deleteThisAndFuture,
  editAllOccurrences, editSingleOccurrence, editThisAndFuture,
  type EventPatch, type WritePlan,
} from '@/lib/recurrence/edit';
import { expandEvents, type Range } from '@/lib/recurrence/expand';
import { syncTasksWithEvents } from '@/db/repositories/taskSchedule';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { ohneFerien } from '@/domain/school/ferien';
import type {
  CalendarEvent, CalendarOccurrence, ID, LocalDateTime, RecurrenceEditScope,
} from '@/domain/types';

export type EventDraft = Omit<
  CalendarEvent, 'id' | 'createdAt' | 'updatedAt' | 'isRecurring' | 'status'
> & Partial<Pick<CalendarEvent, 'status'>>;

// ---------------------------------------------------------------------------
// Lesen
// ---------------------------------------------------------------------------

/**
 * Alle Zeilen, die im Bereich sichtbar werden koennen.
 *
 * IndexedDB kann keine zweiseitige Intervallbedingung indizieren. Deshalb wird
 * ueber `end > from` indiziert vorgefiltert und `start < to` im Speicher
 * geprueft. Ein festes Rueckschaufenster auf `start` waere schneller, wuerde
 * aber lange Termine stillschweigend verlieren.
 *
 * Serien werden vollstaendig geladen: es sind Dutzende, nicht Tausende, und
 * eine Serie kann von ueberall her in den Bereich hineinreichen.
 */
export async function loadEventsForRange(range: Range): Promise<CalendarEvent[]> {
  const [inRange, recurring] = await Promise.all([
    db.events.where('end').above(range.from).toArray(),
    db.events.where('isRecurring').equals(1).toArray(),
  ]);

  const byId = new Map<ID, CalendarEvent>();
  for (const row of inRange) {
    if (String(row.start) < range.to) byId.set(row.id, row);
  }
  for (const row of recurring) byId.set(row.id, row);

  // Ausnahmen aussenstehender Serien nachladen, damit ein Vorkommen nicht
  // faelschlich als regulaer erscheint.
  const seriesIds = [...byId.values()].filter((e) => e.rrule).map((e) => e.id);
  if (seriesIds.length > 0) {
    const overrides = await db.events.where('recurringEventId').anyOf(seriesIds).toArray();
    for (const row of overrides) byId.set(row.id, row);
  }

  return [...byId.values()];
}

export async function getOccurrences(range: Range): Promise<CalendarOccurrence[]> {
  const [events, schule, bestaetigungen] = await Promise.all([
    loadEventsForRange(range),
    db.schoolSettings.get('schule'),
    // Ein Vorkommen kann vor dem Bereich beginnen - einen Tag Vorlauf.
    db.terminBestaetigungen.where('datum').between(addDays(datePart(range.from), -1), datePart(range.to), true, true).toArray(),
  ]);
  // In den Ferien fallen die Schulstunden aus (F-22).
  const liste = ohneFerien(expandEvents(events, range), schule?.ferien ?? []);
  // Bestaetigungen und Erledigt-Status der Zeitbloecke dazulesen (F-29).
  const nachKey = new Map(bestaetigungen.map((b) => [b.id, b]));
  const taskIds = [...new Set(liste.flatMap((o) => (o.sourceTaskId ? [o.sourceTaskId] : [])))];
  const tasks = taskIds.length > 0 ? await db.tasks.bulkGet(taskIds) : [];
  const erledigt = new Map(tasks.flatMap((t) => (t ? [[t.id, t.status === 'erledigt'] as const] : [])));
  const training = await trainingsStand(liste);
  return liste.map((o) => {
    const b = nachKey.get(bestaetigungsKey(o));
    const t = o.training ? training.get(`${o.training.planEinheitId}|${o.training.datum}`) ?? { erledigt: false } : undefined;
    const e = o.sourceTaskId ? erledigt.get(o.sourceTaskId) : t?.erledigt;
    return b || e !== undefined
      ? { ...o, ...(b ? { bestaetigung: b } : {}), ...(e !== undefined ? { blockErledigt: e } : {}), ...(t?.zeit ? { blockZeit: t.zeit } : {}) }
      : o;
  });
}

/**
 * Trainingstermine (F-34 E): fertiges Protokoll der Einheit am Plantag =
 * erledigt, mit der tatsaechlichen Zeit, falls Beginn und Ende erfasst sind.
 */
async function trainingsStand(liste: CalendarOccurrence[]): Promise<Map<string, { erledigt: boolean; zeit?: { start: LocalDateTime; end: LocalDateTime } }>> {
  const tage = [...new Set(liste.flatMap((o) => (o.training ? [o.training.datum] : [])))];
  const out = new Map<string, { erledigt: boolean; zeit?: { start: LocalDateTime; end: LocalDateTime } }>();
  if (!tage.length) return out;
  const protokolle = await db.trainingsprotokolle.where('datum').anyOf(tage).toArray();
  for (const p of protokolle) {
    if (p.status !== 'fertig' || !p.planEinheitId) continue;
    const zeit = p.start && p.ende ? { start: toLocalDateTime(new Date(p.start)), end: toLocalDateTime(new Date(p.ende)) } : undefined;
    out.set(`${p.planEinheitId}|${p.datum}`, { erledigt: true, ...(zeit && zeit.end > zeit.start ? { zeit } : {}) });
  }
  return out;
}

export function getEvent(id: ID): Promise<CalendarEvent | undefined> {
  return db.events.get(id);
}

/** Die Serie eines Vorkommens samt ihrer Ausnahmen. */
export async function getSeriesContext(eventId: ID): Promise<{
  master: CalendarEvent;
  overrides: CalendarEvent[];
} | null> {
  const row = await db.events.get(eventId);
  if (!row) return null;

  const master = row.recurringEventId ? await db.events.get(row.recurringEventId) : row;
  if (!master) return null;

  const overrides = await db.events.where('recurringEventId').equals(master.id).toArray();
  return { master, overrides };
}

// ---------------------------------------------------------------------------
// Schreiben
// ---------------------------------------------------------------------------

export async function createEvent(draft: EventDraft): Promise<CalendarEvent> {
  const ts = nowInstant();
  const event: CalendarEvent = {
    ...draft,
    id: newId(),
    status: draft.status ?? 'confirmed',
    isRecurring: draft.rrule ? 1 : 0,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.events.add(event);
  return event;
}

/** Einzeltermin aendern - ohne Serienlogik. */
export async function updateEvent(id: ID, patch: EventPatch): Promise<void> {
  await db.transaction('rw', db.events, db.tasks, async () => {
    const existing = await db.events.get(id);
    if (!existing) return;
    await db.events.put({
      ...existing,
      ...patch,
      isRecurring: (patch.rrule ?? existing.rrule) ? 1 : 0,
      updatedAt: nowInstant(),
    });
    // Ein verschobener Zeitblock verschiebt seine Aufgabe (F-06).
    await syncTasksWithEvents([id]);
  });
}

/** Termin loeschen - samt Bestaetigungen und Kopplung zur Aufgabe als EIN Papierkorb-Eintrag (F-45). */
export async function deleteEvent(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'events', zeileIds: [id] }, () => db.transaction('rw', db.events, db.tasks, db.terminBestaetigungen, async () => {
    await db.events.delete(id);
    await db.terminBestaetigungen.where('eventId').equals(id).delete();
    await syncTasksWithEvents([id]);
  }));
}

async function applyPlan(plan: WritePlan): Promise<void> {
  await db.transaction('rw', db.events, db.tasks, db.terminBestaetigungen, async () => {
    if (plan.deletes.length) {
      await db.events.bulkDelete(plan.deletes);
      // Bestaetigungen geloeschter Termine und Serien (F-29) gleich mit.
      await db.terminBestaetigungen.where('eventId').anyOf(plan.deletes).delete();
    }
    if (plan.puts.length) await db.events.bulkPut(plan.puts);
    await syncTasksWithEvents([...plan.deletes, ...plan.puts.map((e) => e.id)]);
  });
}

/**
 * Ein Vorkommen bearbeiten - der Modus entscheidet, was geschrieben wird.
 *
 * `occurrenceStart` ist der URSPRUENGLICHE Start des angefassten Vorkommens,
 * nicht der neue. Nur damit laesst sich die betroffene Stelle der Serie
 * eindeutig bestimmen.
 */
export async function editOccurrence(
  eventId: ID,
  occurrenceStart: LocalDateTime,
  patch: EventPatch,
  scope: RecurrenceEditScope,
): Promise<void> {
  const context = await getSeriesContext(eventId);
  if (!context) return;

  // Einzeltermin ohne Serie: der Modus ist bedeutungslos.
  if (!context.master.rrule) {
    await updateEvent(context.master.id, patch);
    return;
  }

  const ctx = {
    master: context.master,
    overrides: context.overrides,
    originalStart: occurrenceStart,
    patch,
    now: nowInstant(),
    newId,
  };

  const plan = scope === 'dieser'
    ? editSingleOccurrence(ctx)
    : scope === 'dieser_und_folgende'
      ? editThisAndFuture(ctx)
      : editAllOccurrences(ctx);

  await applyPlan(plan);
}

/**
 * Ein Vorkommen loeschen. Was der Modus auch schreibt (Ausnahme, Ende der
 * Serie, ganze Serie): es wird EIN Papierkorb-Eintrag (F-45), beim einzelnen
 * Vorkommen mit dem Datum im Titel.
 */
export async function deleteOccurrence(
  eventId: ID,
  occurrenceStart: LocalDateTime,
  scope: RecurrenceEditScope,
): Promise<void> {
  const context = await getSeriesContext(eventId);
  if (!context) return;
  const { master } = context;

  if (!master.rrule) {
    await deleteEvent(master.id);
    return;
  }

  const ctx = {
    master,
    overrides: context.overrides,
    originalStart: occurrenceStart,
    now: nowInstant(),
  };

  const plan = scope === 'dieser'
    ? deleteSingleOccurrence(ctx)
    : scope === 'dieser_und_folgende'
      ? deleteThisAndFuture(ctx)
      : deleteAllOccurrences(ctx);

  const titel = scope === 'alle' ? undefined
    : `${master.title || 'Termin'} ${scope === 'dieser' ? 'am' : 'ab'} ${formatDate(occurrenceStart)}`;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'events', zeileIds: [master.id], titel }, () => applyPlan(plan));
}
